import {Controller} from '@hotwired/stimulus'
import $ from 'jquery'
import {unmountReactNode} from 'utils/reactRoots'

const islandSelector = '[data-controller~="react-island"]'

const resolveWrapper = (sel) => {
  if (!sel)
return null

  return sel.closest(islandSelector)
}

const readComponentValue = (component) => {
  if (!component)
return null

  const hidden = component.querySelector('input[type="hidden"]')
  if (hidden)
return hidden.value

  try {
    const props = JSON.parse(component.dataset.reactIslandPropsValue || '{}')
    const dv = props.defaultValue
    return dv && (Array.isArray(dv) ? dv[0]?.value : dv.value || dv)
  } catch (e) {
    return null
  }
}

const optionForValue = (component, value) => {
  if (!component)
    return null

  try {
    const options
      = JSON.parse(component.dataset.reactIslandPropsValue || '{}').options || []
    return options.find((option) => option.value === value) || null
  } catch (e) {
    return null
  }
}

const setHiddenInputValue = (component, value) => {
  if (!component)
return

  const hidden = component.querySelector('input[type="hidden"]')
  if (hidden)
hidden.value = value
}

const eventTargetsSelector = (event, selector) => {
  const target = event.target
  if (!(target instanceof Element))
return false

  if (target.closest(selector))
return true

  if (
    target.matches(islandSelector)
    && target.querySelector(selector)
  )
    return true

  const wrapper = target.closest(islandSelector)
  return Boolean(wrapper?.querySelector(selector))
}

export default class extends Controller {
  connect() {
    this.boundHandleChange = this.handleChange.bind(this)
    this.element.addEventListener('change', this.boundHandleChange)
    this.animateDependentFields = false

    const needsObserve
      = !this.statusComponent()
      && !this.frequencyComponent()
      && !this.scheduledDateComponent()
    if (needsObserve && typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver((mutations, obs) => {
        if (
          this.statusComponent()
          || this.frequencyComponent()
          || this.scheduledDateComponent()
        ) {
          this.handleStatusChange()
          obs.disconnect()
          this.mutationObserver = null
        }
      })
      this.mutationObserver.observe(this.element, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'data-react-island-component-value',
          'data-react-island-props-value',
        ],
      })
    } else {
      this.initializationTimeout = setTimeout(() => {
        this.initializationTimeout = null
        this.handleStatusChange()
      }, 50)
    }
    this.animateDependentFields = true
  }

  disconnect() {
    if (this.boundHandleChange)
      this.element.removeEventListener('change', this.boundHandleChange)
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
    if (this.initializationTimeout) {
      clearTimeout(this.initializationTimeout)
      this.initializationTimeout = null
    }
  }

  handleChange(event) {
    const target = event.target
    if (!(target instanceof HTMLInputElement) || target.type !== 'hidden')
      return

    if (eventTargetsSelector(event, '.js-frequency-selector')) {
      this.handleFrequencyChange()
      return
    }

    if (eventTargetsSelector(event, '.js-status-selector'))
      this.handleStatusChange()
  }

  componentFor(selector) {
    return resolveWrapper(this.element.querySelector(selector))
  }

  statusComponent() {
    return this.componentFor('.js-status-selector')
  }

  frequencyComponent() {
    return this.componentFor('.js-frequency-selector')
  }

  scheduledDateComponent() {
    return this.componentFor('.js-scheduled-date')
  }

  setDependentFieldVisibility(selector, isVisible) {
    const el = this.element.querySelector(selector)
    if (!el)
return

    const shouldAnimate
      = this.animateDependentFields && process.env.NODE_ENV !== 'test'

    if (shouldAnimate) {
      const $el = $(el)
      if (isVisible)
$el.slideDown(300, 'swing')
      else
$el.slideUp(300, 'swing')
      return
    }

    el.style.display = isVisible ? '' : 'none'
  }

  updateDependentFieldVisibility(status) {
    const showScheduled = status === 'scheduled'
    const showWaiting = status === 'waiting'
    const showDefault = !showScheduled && !showWaiting
    this.setDependentFieldVisibility(
      '.dependent-field.scheduled',
      showScheduled,
    )
    this.setDependentFieldVisibility('.dependent-field.waiting', showWaiting)
    this.setDependentFieldVisibility(
      '.dependent-field.current.completed.someday',
      showDefault,
    )
  }

  handleFrequencyChange() {
    const frequencyComponent = this.frequencyComponent()
    const statusComponent = this.statusComponent()

    if (!frequencyComponent || !statusComponent)
return

    const frequency = readComponentValue(frequencyComponent)
    if (!frequency || frequency === 'none')
return

    const scheduledOption = optionForValue(statusComponent, 'scheduled')
    if (scheduledOption)
      this.setComponentProp(statusComponent, 'defaultValue', scheduledOption)

    const scheduledDateComponent = this.scheduledDateComponent()
    if (scheduledDateComponent) {
      this.setComponentProp(
        scheduledDateComponent,
        'dateString',
        new Date().toISOString(),
      )
    }

    this.updateDependentFieldVisibility('scheduled')
  }

  handleStatusChange() {
    const statusComponent = this.statusComponent()
    const status = readComponentValue(statusComponent)
    const frequencyComponent = this.frequencyComponent()
    const scheduledDateComponent = this.scheduledDateComponent()

    if (status !== 'scheduled' && frequencyComponent) {
      setHiddenInputValue(frequencyComponent, 'none')

      const noneOption = optionForValue(frequencyComponent, 'none')
      this.setComponentProp(frequencyComponent, 'defaultValue', noneOption)

      if (scheduledDateComponent)
        this.setComponentProp(scheduledDateComponent, 'dateString', '')
    }

    this.updateDependentFieldVisibility(status)
  }

  setComponentProp(component, prop, value) {
    if (!this.element || !component)
return
    try {
      const props = JSON.parse(component.dataset.reactIslandPropsValue || '{}')
      props[prop] = value

      if (
        prop === 'defaultValue'
        && (component.dataset.reactClass?.includes('RailsSelect')
          || Object.prototype.hasOwnProperty.call(props, 'value'))
      ) {
        const isRailsSelect
          = component.dataset.reactClass?.includes('RailsSelect')
        props.value
          = isRailsSelect && value && typeof value === 'object'
            ? value.value
            : value
      }

      const serializedProps = JSON.stringify(props)
      const propsAreUnchanged
        = component.dataset.reactIslandPropsValue === serializedProps

      unmountReactNode(component)
      component.dataset.reactIslandPropsValue = serializedProps

      if (propsAreUnchanged)
        this.application.getControllerForElementAndIdentifier(component, 'react-island')?.mountComponent()
    } catch (e) {
      // no-op
    }
  }
}
